// @ts-strict-ignore
/**
 * Base "resource"; doesn't know about where its data comes from
 * (e.g. API calls or in-memory models), but handles common logic
 * like subscription management.
 */

import {Component, PanelLifecycleContext} from 'panel';

import type {Subscribable} from 'common/types/context/subscribable';

export enum ModelStatus {
  /** Object exists, but has never attempted to be populated */
  Idle = `Idle`,
  /** Getting value from an async source */
  Loading = `Loading`,
  /** Value has successfully been loaded */
  Success = `Success`,
  /** Failed to get the value */
  Error = `Error`,
  /** Async fetch succeeded but no object was found */
  NotFound = `NotFound`,
}

/**
 * A single model instance, with metadata about its lifecycle
 * (loading, ready to use, errored out while loading)
 */
export type Model<ModelT> = {
  status: ModelStatus;
  value?: ModelT;
  error?: Error | string;
};

export abstract class ModelResource implements PanelLifecycleContext, Subscribable {
  private subscriptions = [];

  notifySubscribers() {
    for (const subscription of this.subscriptions) {
      subscription();
    }
  }

  subscribe(callback) {
    this.subscriptions.push(callback);
    return this;
  }

  unsubscribe(callback) {
    this.subscriptions = this.subscriptions.filter((cb) => cb !== callback);
    return this;
  }

  _componentSubscriptions = new Map();
  bindToComponent(component: Component<any>) {
    // TODO resourceCustomSubscriptions should probably be built into Panel Contexts
    const customSubscriptions = component[`resourceCustomSubscriptions`] || new Map();
    const callback = customSubscriptions.get(this.constructor) || component.update.bind(component);

    this._componentSubscriptions.set(component, callback);
    this.subscribe(this._componentSubscriptions.get(component));
  }

  unbindFromComponent(component: Component<any>) {
    this.unsubscribe(this._componentSubscriptions.get(component));
  }
}
